import { useEffect, useMemo, useState } from "react";
import Archive from "lucide-react/dist/esm/icons/archive.mjs";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left.mjs";
import BookOpen from "lucide-react/dist/esm/icons/book-open.mjs";
import Headphones from "lucide-react/dist/esm/icons/headphones.mjs";
import RotateCcw from "lucide-react/dist/esm/icons/rotate-ccw.mjs";
import ShieldCheck from "lucide-react/dist/esm/icons/shield-check.mjs";
import type { PodcastEpisode, PodcastEpisodeSource, ReaderPreferences } from "../types";
import { ArtworkImage } from "./ArtworkImage";

export function PodcastEpisodePane({
  episode,
  loading,
  error,
  online,
  preferences,
  busySource,
  archiveBusy,
  restoreAllBusy,
  artworkBusy,
  onBack,
  onOpenSource,
  onKeep,
  onRestore,
  onArchive,
  onRestoreAll,
  onGenerateArtwork,
  onRetry,
}: {
  episode?: PodcastEpisode;
  loading: boolean;
  error?: string;
  online: boolean;
  preferences: ReaderPreferences;
  busySource: string;
  archiveBusy: boolean;
  restoreAllBusy: boolean;
  artworkBusy: boolean;
  onBack: () => void;
  onOpenSource: (source: PodcastEpisodeSource) => void;
  onKeep: (source: PodcastEpisodeSource) => void;
  onRestore: (source: PodcastEpisodeSource) => void;
  onArchive: (itemIds: string[]) => void;
  onRestoreAll: () => void;
  onGenerateArtwork: () => void;
  onRetry?: () => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [reviewingArchive, setReviewingArchive] = useState(false);
  const sources = episode?.sources || [];

  useEffect(() => {
    setSelectedIds(new Set((episode?.sources || []).filter(defaultSelectedSource).map((source) => source.item_id)));
    setReviewingArchive(false);
  }, [episode?.id, episode?.status, episode?.sources]);

  const selectedSources = useMemo(
    () => sources.filter((source) => selectableSource(source) && selectedIds.has(source.item_id)),
    [selectedIds, sources],
  );
  const archivedCount = sources.filter((source) => source.state === "archived").length;
  const classes = ["reader-pane", "podcast-episode-pane", `reader-pane--size-${preferences.typeSize}`, `reader-pane--width-${preferences.width}`, `reader-pane--theme-${preferences.theme}`].join(" ");

  function toggleSource(source: PodcastEpisodeSource) {
    if (!selectableSource(source)) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      next.has(source.item_id) ? next.delete(source.item_id) : next.add(source.item_id);
      return next;
    });
    setReviewingArchive(false);
  }

  return (
    <section className={classes} aria-label={episode?.title || "Earlier Briefing receipt"}>
      <header className="reader-toolbar">
        <button type="button" onClick={onBack} className="reader-toolbar__back"><ArrowLeft aria-hidden="true" />Back to {episode?.status === "archive" ? "Archive" : "Inbox"}</button>
        <div className="reader-toolbar__actions">
          <span className="brief-trust"><ShieldCheck aria-hidden="true" />Source checked</span>
          {episode?.status === "archive" && archivedCount ? <button type="button" disabled={!online || restoreAllBusy} onClick={onRestoreAll}><RotateCcw aria-hidden="true" /><span>{restoreAllBusy ? "Restoring…" : "Restore all"}</span></button> : null}
        </div>
      </header>
      <div className="reader-scroll">
        {loading && !episode ? <p className="reader-loading" role="status">Opening episode receipt…</p> : null}
        {error ? <div className="reader-state inline-recovery" role="alert"><p>{error}</p>{onRetry ? <button type="button" onClick={onRetry}>Retry</button> : null}</div> : null}
        {episode ? (
          <article className="article-sheet podcast-episode-sheet">
            <header className="article-header">
              <p>EARLIER BRIEFING</p>
              <h1 tabIndex={-1} data-reader-route-heading>{episode.title}</h1>
              <div>{episode.subtitle}</div>
            </header>
            {episode.artwork_state === "ready" && episode.artwork ? <ArtworkImage artwork={episode.artwork} className="podcast-episode-artwork" loading="lazy" /> : null}
            <section className="podcast-variants" aria-label="Episode voices">
              <Headphones aria-hidden="true" />
              <div>
                <strong>In your private podcast feed</strong>
                <span>{variantLabel(episode)} · source-checked script</span>
              </div>
              {needsArtwork(episode) ? (
                <button type="button" disabled={!online || artworkBusy} onClick={onGenerateArtwork}>
                  {artworkBusy ? "Generating artwork…" : "Generate artwork"}
                </button>
              ) : null}
            </section>
            {episode.chapters?.length ? (
              <section className="podcast-chapters" aria-label="Episode chapters">
                {episode.chapters.map((chapter) => (
                  <article className="podcast-chapter" key={chapter.id}>
                    {chapter.artwork_state === "ready" && chapter.artwork ? <ArtworkImage artwork={chapter.artwork} className="podcast-chapter__artwork" /> : null}
                    <div><span>Chapter {chapter.position + 1}</span><strong>{chapter.title}</strong></div>
                  </article>
                ))}
              </section>
            ) : null}
            <section className="brief-source-ledger podcast-source-ledger" aria-labelledby="podcast-source-heading">
              <h2 id="podcast-source-heading">Episode receipt</h2>
              <p>Archive only the originals this episode covered. Inbox sources start selected; Queue sources stay off unless you choose them. Read-original recommendations cannot be archived here.</p>
              {sources.map((source) => {
                const selectable = episode.status === "inbox" && selectableSource(source);
                return (
                  <article key={source.item_id} className={`brief-source-row podcast-source-row${source.editorial_role === "read_original" ? " podcast-source-row--read-original" : ""}`}>
                    <div className="podcast-source-copy">
                      <label className="podcast-source-selection">
                        <input
                          type="checkbox"
                          checked={selectable && selectedIds.has(source.item_id)}
                          disabled={!selectable}
                          onChange={() => toggleSource(source)}
                          aria-label={`Archive ${source.title}`}
                        />
                        <span>{source.editorial_role === "covered_here" ? "Covered here" : "Read original"} · {source.original_status === "inbox" ? "Inbox" : "Queue"}</span>
                      </label>
                      <strong>{source.title}</strong>
                      <span>{source.site_name}{source.state !== "pending" ? ` · ${sourceStateLabel(source.state)}` : ""}</span>
                      {source.takeaway ? <p>{source.takeaway}</p> : null}
                    </div>
                    <div className="brief-source-actions">
                    <button type="button" data-reader-origin-key={`podcast:${source.item_id}`} onClick={() => onOpenSource(source)}><BookOpen aria-hidden="true" />Open original</button>
                      {episode.status === "inbox" && source.editorial_role === "covered_here" && source.state === "pending" ? <button type="button" disabled={!online || busySource === source.item_id} onClick={() => onKeep(source)}>{busySource === source.item_id ? "Keeping…" : `Keep in ${source.original_status === "queue" ? "Queue" : "Inbox"}`}</button> : null}
                      {episode.status === "archive" && source.state === "archived" ? <button type="button" disabled={!online || busySource === source.item_id} onClick={() => onRestore(source)}><RotateCcw aria-hidden="true" />{busySource === source.item_id ? "Restoring…" : `Restore to ${source.original_status === "queue" ? "Queue" : "Inbox"}`}</button> : null}
                    </div>
                  </article>
                );
              })}
            </section>
          </article>
        ) : null}
      </div>
      {episode?.status === "inbox" && reviewingArchive ? (
        <footer className="reader-bottom-actions brief-disposition podcast-disposition podcast-disposition--confirm" aria-live="polite">
          <button type="button" disabled={!online || archiveBusy || !selectedSources.length} onClick={() => onArchive(selectedSources.map((source) => source.item_id))}><Archive aria-hidden="true" />{archiveBusy ? "Archiving…" : `Confirm archive · ${selectedSources.length}`}</button>
          <button type="button" disabled={archiveBusy} onClick={() => setReviewingArchive(false)}>Cancel · changed items stay put</button>
        </footer>
      ) : episode?.status === "inbox" ? (
        <footer className="reader-bottom-actions brief-disposition podcast-disposition">
          <button type="button" disabled={!online || archiveBusy || !selectedSources.length} onClick={() => setReviewingArchive(true)}><Archive aria-hidden="true" />Review archive · {selectedSources.length}</button>
          <span>Nothing moves until you review and confirm the selection.</span>
        </footer>
      ) : archivedCount ? (
        <footer className="reader-bottom-actions brief-disposition podcast-disposition"><button type="button" disabled={!online || restoreAllBusy} onClick={onRestoreAll}><RotateCcw aria-hidden="true" />{restoreAllBusy ? "Restoring…" : `Restore all · ${archivedCount}`}</button><span>Only originals archived by this episode can be restored here.</span></footer>
      ) : null}
    </section>
  );
}

function needsArtwork(episode: PodcastEpisode): boolean {
  return episode.artwork_state !== "ready" || episode.chapters?.length !== 3 || episode.chapters.some((chapter) => chapter.artwork_state !== "ready");
}

function selectableSource(source: PodcastEpisodeSource): boolean {
  return source.editorial_role === "covered_here" && source.state === "pending";
}

function defaultSelectedSource(source: PodcastEpisodeSource): boolean {
  return selectableSource(source) && source.default_selected && source.original_status === "inbox";
}

function variantLabel(episode: PodcastEpisode): string {
  const voices = episode.variants.map((variant) => variant.voice.charAt(0).toUpperCase() + variant.voice.slice(1));
  return voices.length ? voices.join(" + ") : "Cedar";
}

function sourceStateLabel(state: PodcastEpisodeSource["state"]): string {
  if (state === "skipped_changed") return "Changed · not archived";
  if (state === "not_eligible") return "Not eligible";
  if (state === "kept") return "Kept";
  if (state === "archived") return "Archived by this episode";
  if (state === "restored") return "Restored";
  return "Ready";
}
